import { Space, Spin } from "antd";
import { ComponentProps } from "react";
import { useEffectOnce } from "usehooks-ts";
import { signIn, useSession as useNextSession } from "next-auth/react";
import {
  useApplicationRuntime,
  ApplicationContext,
} from "src/hooks/useApplication";
import { AppScreen } from "./AppScreen";
import React, { useRef, useEffect } from "react";
import { useSubscriptionCheck } from "src/hooks/useSubscriptionCheck";

export function AuthedPage(
  props: ComponentProps<typeof AppScreen> & {
    requireAdmin?: boolean;
  }
) {
  const session = useNextSession();
  useEffectOnce(() => {
    if (process.env.NODE_ENV === "development") {
      invertFavicon();
    }
  });
  if (session.status === "loading") return null;
  if (session.status === "authenticated") return <AuthedAppScreen {...props} />;
  signIn();
  return null;
}

function AuthedAppScreen(
  props: ComponentProps<typeof AppScreen> & { requireAdmin?: boolean }
) {
  const appContext = useApplicationRuntime();
  useSubscriptionCheck();

  const prevAppContextRef = useRef(appContext);
  useEffect(() => {
    prevAppContextRef.current = appContext;
  });

  if (
    !appContext ||
    (props.orgId && !appContext.ready) ||
    (props.orgId && !appContext.org_id) ||
    (props.requireAdmin && !appContext.role)
  )
    return (
      <Space
        style={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin />
      </Space>
    );
  const isAdmin = appContext.user?.superuser || appContext.role === "admin";
  if (props.requireAdmin && !isAdmin) throw new Error("unauthorized access");

  return (
    <ApplicationContext.Provider value={appContext}>
      <AppScreen {...props} />
    </ApplicationContext.Provider>
  );
}

async function invertFavicon() {
  if (isInverted()) return;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const img = await createFaviconImage();
  const { width, height } = img;

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(img, 0, 0, width, height);

  const invertedData = invertImageData(ctx.getImageData(0, 0, width, height));
  ctx.putImageData(invertedData, 0, 0);
  updateFavicon(canvas.toDataURL("image/png"));
}

function isInverted() {
  const link = document.querySelector("link[rel='icon'][title='inverted']");
  return Boolean(link);
}

function updateFavicon(href: string) {
  const link: HTMLLinkElement = document.createElement("link");
  link.href = href;
  link.rel = "icon";
  link.title = "inverted";
  document.head.append(link);
}

async function createFaviconImage(): Promise<HTMLImageElement> {
  return new Promise((resolve) => {
    const img = document.createElement("img");
    img.src = "/favicon.ico";
    img.width = 48;
    img.height = 48;
    img.addEventListener("load", () => resolve(img));
  });
}

function invertImageData(imageData: ImageData): ImageData {
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255 - data[i];
    data[i + 1] = 255 - data[i + 1];
    data[i + 2] = 255 - data[i + 2];
  }
  return imageData;
}
